import i18next from 'i18next';
import {List, Set, Map} from 'immutable';

import {roundToDecimals} from 'web-app/util/math';
import BillPayerBehaviors from 'web-app/behaviors/bill-payer-behavior-ids';

import BillPayerShare from './common/bill-payer-share';
import SplitConfiguration from './common/split-configuration';

// Group bill payers by type so that when the type changes a new group is created
const getConfigurationGroups = splitConfiguration =>
    splitConfiguration.shares.reduce((reduction, share) => {
        // If there are no previous groups or the previous group has a different type than the current share
        // add a new group to the list
        if (reduction.size === 0 || reduction.last().first().type !== share.type) {
            return reduction.push(List([share]));
        }
        // otherwise add the current share to the last group
        else {
            return reduction.update(-1, group => group.push(share));
        }
    }, List());

export const addBillPayerToSplitConfiguration = (splitConfiguration, billPayer) => {
    if (splitConfiguration.shares.find(billPayerShare => billPayerShare.billPayerId === billPayer.billPayerId)) {
        return splitConfiguration;
    }

    const newBillPayerShare = BillPayerShare.fromAPI({billPayerId: billPayer.billPayerId});
    const newSplitConfiguration = splitConfiguration.merge({
        shares: splitConfiguration.shares.push(newBillPayerShare),
    });

    return divideShareEqually(newSplitConfiguration);
};

export const removeBillPayerFromSplitConfiguration = (splitConfiguration, billPayerId) => {
    const filteredSplitConfiguration = splitConfiguration.merge({
        shares: splitConfiguration.shares.filter(billPayerShare => billPayerShare.billPayerId !== billPayerId),
    });

    // If there are no shares there are no bill payers to divide share across
    if (filteredSplitConfiguration.shares.size === 0) {
        return filteredSplitConfiguration;
    }

    return divideShareEqually(filteredSplitConfiguration);
};

export const divideShareEqually = splitConfiguration => {
    const shares = getConfigurationGroups(splitConfiguration)
        .map((group, i, list) => {
            const isLastGroup = i === list.size - 1;
            if (group.first().type !== BillPayerShare.CONSTANTS.TYPES.PERCENTAGE || !isLastGroup) {
                return group;
            }

            return group.map(billPayerShare =>
                billPayerShare.merge({
                    share: 1 / group.size,
                }),
            );
        })
        .flatten(true);

    return splitConfiguration.merge({
        shares,
    });
};

/**
 * Takes a split configuration that has a split that doesn't add up to 100% and evens it out. The ids in the
 * locked ids array will not have their share changed
 * @param splitConfigurationGroup
 * @param lockedIds
 * @returns splitConfiguration
 */
const evenOutUnevenSplit = (splitConfigurationGroup, lockedIds) => {
    const sharesToUpdate = splitConfigurationGroup.filter(bps => !lockedIds.includes(bps.billPayerId));
    if (sharesToUpdate.size === 0) {
        return splitConfigurationGroup;
    }
    const lockedShares = splitConfigurationGroup.filter(bps => lockedIds.includes(bps.billPayerId));
    const lockedShareTotal = lockedShares.reduce((reduction, bps) => {
        return reduction + bps.share;
    }, 0);

    let singleShare = (1 - lockedShareTotal) / sharesToUpdate.size;
    singleShare = Math.max(singleShare, 0);

    return splitConfigurationGroup.map(bps => {
        if (lockedIds.includes(bps.billPayerId)) {
            return bps;
        }

        return bps.merge({
            share: singleShare,
        });
    });
};

const groupIs100PercentOrMore = group => group.reduce((reduction, current) => reduction + current.share, 0) >= 1;

const getValidationErrors = groupedConfigurations =>
    groupedConfigurations
        .map((group, i) => {
            const isLast = i === groupedConfigurations.size - 1;
            if (isLast && group.first().type === BillPayerShare.CONSTANTS.TYPES.FIXED) {
                return i18next.t('billPayer.splitErrors.lastMustBePercentage');
            } else if (
                !isLast &&
                group.first().type === BillPayerShare.CONSTANTS.TYPES.PERCENTAGE &&
                groupIs100PercentOrMore(group)
            ) {
                return i18next.t('billPayer.splitErrors.onlyLastCanBe100Percent');
            }
            return null;
        })
        .filter(a => a)
        .toSet()
        .toList();

export const updateAndValidateConfiguration = (splitConfiguration, lockedIds) => {
    // If there are no shares there is no reason to update or validate
    if (splitConfiguration.shares.size === 0) {
        return {
            validationErrors: List(),
            splitConfiguration,
        };
    }
    const groupedConfigurations = getConfigurationGroups(splitConfiguration).map((group, i, list) => {
        const isLastGroup = i === list.size - 1;
        if (
            group.first().type === BillPayerShare.CONSTANTS.TYPES.PERCENTAGE &&
            (isLastGroup || groupIs100PercentOrMore(group))
        ) {
            return evenOutUnevenSplit(group, lockedIds);
        }

        return group;
    });
    const validationErrors = getValidationErrors(groupedConfigurations);

    const lastGroup = groupedConfigurations.last();
    const restGroups = groupedConfigurations.pop();
    const evenedLastGroup =
        lastGroup.first().type === BillPayerShare.CONSTANTS.TYPES.PERCENTAGE
            ? evenOutUnevenSplit(lastGroup, lockedIds)
            : lastGroup;

    const newShares = restGroups.flatten(true).concat(evenedLastGroup);

    return {
        validationErrors,
        splitConfiguration: new SplitConfiguration.record({
            shares: newShares,
        }),
    };
};

// Get the new locked bill payer ids based on the current set, configuration and the new id that needs to be added
export const getLockedIds = (currentLockedIds, billPayerId, splitConfiguration) => {
    const groupedConfigurations = getConfigurationGroups(splitConfiguration);

    return groupedConfigurations
        .map(group => {
            // If there is only 1 bill payer in a group it can't be locked
            if (group.size === 1) {
                return null;
            }
            const groupIds = group.map(share => share.billPayerId).toSet();

            // If the current group doesn't contain the new bill payer,
            // return the ids from the group already locked
            if (!groupIds.includes(billPayerId)) {
                return groupIds.intersect(currentLockedIds);
            }

            // Get the bill payers that are not locked in the group
            const notLockedIds = groupIds.subtract(currentLockedIds);

            // If there is no change in size from groupIds to notLockedIds that means that no bill payers has been locked in this group
            // or if the size of the remaining bill payers are 1 (or less) this means that all the bill payers has been updated in the group
            // then only lock the new bill payer
            if (notLockedIds.size === groupIds.size || (notLockedIds.size <= 1 && notLockedIds.includes(billPayerId))) {
                return Set([billPayerId]);
            }

            // Otherwise return the currently locked bill payers and add the new
            return groupIds.intersect(currentLockedIds).add(billPayerId);
        })
        .filter(a => a)
        .flatten(true)
        .toSet();
};

const calcPercentageGroupPrices = (group, total) => {
    const exampleMap = Map(group.map(bps => [bps.billPayerId, bps.share * total]));
    const exampleSum = exampleMap.reduce((sum, example) => sum + example, 0);

    return {
        remainder: total - exampleSum,
        exampleMap,
    };
};

const calcFixedGroupPrices = (group, total) => {
    return group.reduce(
        (reduction, bps) => {
            const roundedValue = roundToDecimals(Math.max(bps.share, 0), 2);

            if (roundedValue > reduction.remainder) {
                return {
                    remainder: 0,
                    exampleMap: reduction.exampleMap.set(bps.billPayerId, reduction.remainder),
                };
            }

            return {
                remainder: reduction.remainder - roundedValue,
                exampleMap: reduction.exampleMap.set(bps.billPayerId, roundedValue),
            };
        },
        {
            remainder: total,
            exampleMap: Map(),
        },
    );
};

export const calculateExample = (splitConfiguration, exampleTotal) => {
    return getConfigurationGroups(splitConfiguration).reduce(
        (reduction, group) => {
            const groupExample =
                group.first().type === BillPayerShare.CONSTANTS.TYPES.PERCENTAGE
                    ? calcPercentageGroupPrices(group, reduction.remainder)
                    : calcFixedGroupPrices(group, reduction.remainder);

            return {
                remainder: groupExample.remainder,
                exampleMap: reduction.exampleMap.merge(groupExample.exampleMap),
            };
        },
        {
            remainder: exampleTotal,
            exampleMap: Map(),
        },
    ).exampleMap;
};

export const canEditBillPayer = billPayerBehaviors => {
    return (
        billPayerBehaviors.includes(BillPayerBehaviors.CanEditBillPayer) ||
        billPayerBehaviors.includes(BillPayerBehaviors.LimitedEditBillPayer)
    );
};
